import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    inject,
    Injector,
    OnInit,
    signal,
    ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DatePicker } from 'primeng/datepicker';
import { CardModule } from 'primeng/card';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { RichTextEditorComponent } from '@shared/components/rich-text-editor/rich-text-editor.component';
import { CmsService } from '@shared/services/cms.service';
import {
    ICmsCategory,
    ICmsContentPage,
    ICmsFeatureBlock,
    ICmsGallery,
    ICmsLanguage,
    ICmsLocalizedPageContent,
    ICmsPort,
    IUpdatedCmsContentPage
} from '@shared/interfaces/cms.interface';
import { FileUpload } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { switchMap, of, finalize } from 'rxjs';
import { environment } from '@environments/environment';
import { CMS_GALLERY_TYPES } from '@shared/contants/variables';
import { LoadingService } from '@services/loading.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { DialogService } from 'primeng/dynamicdialog';
import { AddInfoBoxDialogComponent } from './add-info-box-dialog/add-info-box-dialog.component';

dayjs.extend(utc);

interface UploadEvent {
    originalEvent: Event;
    files: File[];
}

@Component({
    selector: 'app-content-create-edit',
    templateUrl: './content-create-edit.component.html',
    styleUrls: ['./content-create-edit.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        InputNumberModule,
        SelectModule,
        RichTextEditorComponent,
        TabsModule,
        ToggleSwitchModule,
        FileUpload,
        DatePicker,
        DragDropModule,
        CardModule
    ],
    providers: [DialogService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentCreateEditComponent implements OnInit {
    private readonly maxImageUploadSizeBytes = 5 * 1024 * 1024;
    private readonly minSearchBgImageWidth = 1700;

    contentForm!: FormGroup;
    languages = signal<ICmsLanguage[]>([]);
    categories = signal<ICmsCategory[]>([]);
    activeTabIndex = signal(0);
    uploadedFiles: any[] = [];
    contentId: number = 0;
    isEditMode = false;
    contentPage = signal<ICmsContentPage | null>(null);
    gallery = signal<ICmsGallery[]>([]);
    smallImageUrl = environment.smallImageUrl;
    bigImageUrl = environment.bigImageUrl;
    apiUrl = environment.apiUrl;
    uploading = signal(false);
    videoUrl: string = '';
    isInvalidYoutubeUrl = signal(false);
    isDeletingImage = signal(false);
    isScheduled = signal(false);
    ports = signal<ICmsPort[]>([]);
    portLoading = signal(false);
    ogImageFileName = signal<string | null>(null);
    searchBgImageFileName = signal<string | null>(null);
    uploadingOgImage = signal(false);
    deletingOgImage = signal(false);
    uploadingSearchBgImage = signal(false);
    deletingSearchBgImage = signal(false);
    scheduledDate = signal<Date | null>(null);

    // Dependencies
    private fb = inject(FormBuilder);
    private router = inject(Router);
    private cmsService = inject(CmsService);
    private messageService = inject(MessageService);
    private route = inject(ActivatedRoute);
    private loadingService = inject(LoadingService);
    private dialogService = inject(DialogService);
    private cdr = inject(ChangeDetectorRef);
    private injector = inject(Injector);
    private destroyRef = inject(DestroyRef);

    // Form değerlerini takip eden signal (template'te reactive rendering için)
    formValues = signal<any>({});

    @ViewChild('fileUploader') fileUploader!: FileUpload;

    constructor() {}

    ngOnInit(): void {
        this.initForm();
        this.loadCategories();
        this.loadPorts();

        // Form değerlerini takip eden signal'i initialize et
        this.formValues.set(this.contentForm.value);
        this.contentForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.formValues.set(value);
        });

        // Check if we're in edit mode by looking for an ID parameter
        this.route.paramMap
            .pipe(
                switchMap((params) => {
                    const id = params.get('id') as number | null;
                    if (id) {
                        this.contentId = id;
                        this.isEditMode = true;
                        this.loadingService.loadingOn();
                        return this.cmsService.contentPagesGetById(id);
                    }
                    return of(null);
                })
            )
            .subscribe({
                next: (contentPage) => {
                    if (contentPage) {
                        this.contentPage.set(contentPage);
                        this.populateForm(contentPage);
                    }
                    this.loadingService.loadingOff();
                }
            });
    }

    async uploadSearchBgImage(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        if (!this.validateImageFileSize(file, 'Search background image')) {
            input.value = '';
            return;
        }
        if (!(await this.validateSearchBgImageWidth(file))) {
            input.value = '';
            return;
        }

        this.uploadingSearchBgImage.set(true);
        this.cmsService
            .uploadSearchBgImage(this.contentId, file)
            .pipe(
                finalize(() => {
                    this.uploadingSearchBgImage.set(false);
                    input.value = '';
                })
            )
            .subscribe({
                next: (response) => {
                    this.searchBgImageFileName.set(response.fileName);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Search background image uploaded successfully'
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to upload search background image'
                    });
                }
            });
    }

    deleteSearchBgImage(): void {
        this.deletingSearchBgImage.set(true);
        this.cmsService
            .deleteSearchBgImage(this.contentId)
            .pipe(
                finalize(() => {
                    this.deletingSearchBgImage.set(false);
                })
            )
            .subscribe({
                next: () => {
                    this.searchBgImageFileName.set(null);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Search background image deleted successfully'
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete search background image'
                    });
                }
            });
    }

    get utcNow(): string {
        return dayjs().utc().format('DD.MM.YYYY HH:mm');
    }

    /**
     * Populates the form fields with the selected content page data.
     * Updates each language's status and localized content fields in the form.
     * Ensures type safety and prevents patching arrays into scalar fields.
     * @param contentPage The content page data to populate the form with.
     */
    populateForm(contentPage: ICmsContentPage): void {
        const localizedContentsArray = this.contentForm.get('localizedPageContents') as FormArray;
        if (!contentPage?.localizedPageContents || !Array.isArray(contentPage.localizedPageContents)) return;

        this.gallery.set(contentPage.gallery ?? []);
        this.ogImageFileName.set(contentPage.ogImageFileName ?? null);
        this.searchBgImageFileName.set(contentPage.searchBgImageFileName ?? null);
        contentPage.localizedPageContents.forEach((content) => {
            const langIndex = this.languages().findIndex((lang) => lang.locale === content.language?.locale);
            if (langIndex !== -1 && localizedContentsArray.at(langIndex)) {
                // Update language status
                this.languages.update((langs) => {
                    langs[langIndex].status = !!content.active;
                    return [...langs];
                });
                // Patch localized content fields
                localizedContentsArray.at(langIndex).patchValue({
                    id: content.id ?? null,
                    language: content.language ?? null,
                    languageId: content.language?.id ?? null,
                    title: content.title ?? '',
                    content: content.content ?? '',
                    metaTitle: content.metaTitle ?? '',
                    metaDescription: content.metaDescription ?? '',
                    keywords: content.keywords ?? '',
                    active: !!content.active,
                    url: content.url ?? '',
                    featureBlocks: content.featureBlocks ?? []
                });
            }
        });

        // Populate the main form fields
        this.contentForm.patchValue({
            category: contentPage.category ?? null,
            fromPortCode: contentPage.fromPortCode ?? null,
            toPortCode: contentPage.toPortCode ?? null,
            sitemapPriority: contentPage.sitemapPriority ?? null
        });

        if (contentPage.scheduledDate) {
            this.isScheduled.set(true);
            this.scheduledDate.set(new Date(contentPage.scheduledDate));
            this.contentForm.patchValue({
                scheduledDate: new Date(contentPage.scheduledDate) ?? null
            });
        }
    }

    /**
     * Get the FormArray for localized page contents
     */
    get localizedPageContents(): FormArray {
        return this.contentForm.get('localizedPageContents') as FormArray;
    }

    getAbsoluteUrlForLocale(locale: string): string {
        const page = this.contentPage();
        if (!page?.localizedPageContents) return '';
        const match = page.localizedPageContents.find((content) => content.language?.locale === locale && content.absoluteUrl);
        const fallback = page.localizedPageContents.find((content) => content.absoluteUrl);
        return (match ?? fallback)?.absoluteUrl || '';
    }

    /**
     * Helper method to get FormGroup from FormArray
     */
    getFormGroupAt(code: string): FormGroup {
        const formArray = this.contentForm?.get('localizedPageContents') as FormArray;
        const index = formArray.controls.findIndex((control) => {
            const language = control.get('language')?.value;
            return language && language.locale === code;
        });
        if (index !== -1) {
            return formArray.at(index) as FormGroup;
        }
        // Return an empty form group if the index is out of bounds
        return this.fb.group({});
    }

    /**
     * Load categories from the CMS service
     */
    loadCategories(): void {
        this.cmsService.categoriesGetAll().subscribe({
            next: (categories) => {
                this.categories.set(categories);
            }
        });
    }

    loadPorts(): void {
        this.portLoading.set(true);
        this.cmsService
            .getPorts()
            .pipe(
                finalize(() => {
                    this.portLoading.set(false);
                })
            )
            .subscribe({
                next: (ports) => {
                    this.ports.set(ports);
                },
                error: () => {
                    this.ports.set([]);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load ports'
                    });
                }
            });
    }

    initForm(): void {
        this.contentForm = this.fb.group({
            scheduledDate: [''],
            category: ['', Validators.required],
            fromPortCode: [null, [Validators.maxLength(3)]],
            toPortCode: [null, [Validators.maxLength(3)]],
            sitemapPriority: [null, [Validators.min(0), Validators.max(1)]],
            localizedPageContents: this.fb.array([])
        });
        const localizedContentsArray = this.contentForm.get('localizedPageContents') as FormArray;
        this.cmsService.languagesGetAll().subscribe({
            next: (languages) => {
                const initializedLanguages = languages.map((lang) => ({
                    ...lang,
                    status: false
                }));
                this.languages.set(initializedLanguages);

                initializedLanguages.forEach((lang) => {
                    const newFormGroup = this.createLocalizedContentFormGroup();
                    newFormGroup.patchValue({
                        language: { locale: lang.locale, id: lang.id },
                        languageId: lang.id,
                        active: false
                    });
                    localizedContentsArray.push(newFormGroup);
                });
            }
        });
    }

    /**
     * Create a form group for localized content
     */
    createLocalizedContentFormGroup(content?: ICmsLocalizedPageContent): FormGroup {
        const formGroup = this.fb.group({
            id: [content?.id || null],
            language: [content?.language || null],
            languageId: [content?.language.id || null],
            title: [content?.title || '', this.requiredWhenActive],
            content: [content?.content || ''],
            metaTitle: [content?.metaTitle || ''],
            metaDescription: [content?.metaDescription || ''],
            keywords: [content?.keywords || ''],
            active: [content?.active || false],
            url: [content?.url || '', this.requiredWhenActive],
            featureBlocks: [content?.featureBlocks || []]
        });

        // İçerik aktiflik durumu değiştiğinde title ve url'in zorunluluk doğrulamasını yeniden çalıştır
        formGroup
            .get('active')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                formGroup.get('title')?.updateValueAndValidity();
                formGroup.get('url')?.updateValueAndValidity();
            });

        return formGroup;
    }

    /**
     * İçerik aktifse (active) alanı zorunlu yapar; aktif değilse zorunluluk uygulanmaz.
     */
    private requiredWhenActive(control: AbstractControl): ValidationErrors | null {
        const isActive = control.parent?.get('active')?.value;
        if (!isActive) {
            return null;
        }
        const value = control.value;
        const isEmpty = value === null || value === undefined || `${value}`.trim() === '';
        return isEmpty ? { required: true } : null;
    }

    savePage(): void {
        if (this.contentForm.valid) {
            this.loadingService.loadingOn();

            const localizedPageContents = this.contentForm.get('localizedPageContents')?.value;

            localizedPageContents.forEach((content: ICmsLocalizedPageContent) => {
                if (!content.id) {
                    delete content.id;
                }
            });

            const payload: IUpdatedCmsContentPage = {
                scheduleDate: this.isScheduled() ? dayjs(this.contentForm.get('scheduledDate')?.value).format('YYYY-MM-DDTHH:mm:ss') : null,
                categoryId: this.contentForm.get('category')?.value.id,
                fontSize: this.contentPage()?.fontSize || 14,
                localizedPageContents,
                id: this.contentId,
                gallery: this.gallery(),
                ogImageFileName: this.ogImageFileName(),
                fromPortCode: this.normalizePortCode(this.contentForm.get('fromPortCode')?.value),
                toPortCode: this.normalizePortCode(this.contentForm.get('toPortCode')?.value),
                searchBgImageFileName: this.searchBgImageFileName(),
                sitemapPriority: this.contentForm.get('sitemapPriority')?.value ?? null
            };

            if (this.isEditMode) {
                this.cmsService
                    .contentPageUpdate(this.contentId, payload)
                    .pipe(
                        finalize(() => {
                            this.loadingService.loadingOff();
                        })
                    )
                    .subscribe({
                        next: (response) => {
                            console.log('Page updated successfully:', response);
                            this.contentPage.set(response);
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'The page has been updated successfully.'
                            });
                        }
                    });
            } else {
                this.cmsService
                    .contentPageCreate(payload)
                    .pipe(
                        finalize(() => {
                            this.loadingService.loadingOff();
                        })
                    )
                    .subscribe({
                        next: (response) => {
                            this.router.navigate(['/cms']);
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'The page has been created successfully.'
                            });
                        }
                    });
            }

            // Publish page logic would go here
        } else {
            this.markFormGroupTouched(this.contentForm);
        }
    }

    clearAll(): void {
        this.contentForm.reset();
    }

    goBack(): void {
        this.router.navigate(['/cms']);
    }

    normalizePortCode(value: string | null | undefined): string | null {
        const normalizedValue = value?.trim().toUpperCase();
        return normalizedValue ? normalizedValue : null;
    }

    private validateImageFileSize(file: File, imageLabel: string): boolean {
        if (file.size <= this.maxImageUploadSizeBytes) {
            return true;
        }

        this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `${imageLabel} size cannot exceed 5MB`
        });

        return false;
    }

    private validateSearchBgImageWidth(file: File): Promise<boolean> {
        return new Promise((resolve) => {
            const image = new Image();
            const imageUrl = URL.createObjectURL(file);

            image.onload = () => {
                console.log('image.width', image.width);
                const isValid = image.width >= this.minSearchBgImageWidth;
                if (!isValid) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: `Search background image width must be at least ${this.minSearchBgImageWidth}px`
                    });
                }
                URL.revokeObjectURL(imageUrl);
                resolve(isValid);
            };

            image.onerror = () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Invalid image file'
                });
                URL.revokeObjectURL(imageUrl);
                resolve(false);
            };

            image.src = imageUrl;
        });
    }

    // Helper method to mark all controls as touched
    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach((control) => {
            control.markAsTouched();
            if ((control as any).controls) {
                this.markFormGroupTouched(control as FormGroup);
            }
        });
    }

    // Handle tab change
    onTabChange(value: string | number | undefined): void {
        this.activeTabIndex.set(typeof value === 'number' ? value : Number(value ?? 0));
    }

    // Toggle publish status
    togglePublishStatus(index: number, checked: boolean): void {
        this.languages.update((langs) => {
            langs[index].status = checked;
            return [...langs];
        });

        // Update the form array's active status
        const localizedContentsArray = this.contentForm.get('localizedPageContents') as FormArray;
        if (localizedContentsArray && localizedContentsArray.length > index) {
            const formGroup = localizedContentsArray.at(index) as FormGroup;
            formGroup.get('active')?.setValue(checked);
        }
    }

    toggleScheduledStatus(isScheduled: boolean): void {
        this.isScheduled.set(isScheduled);
        const scheduledDate = this.contentForm.get('scheduledDate');
        if (isScheduled) {
            scheduledDate?.setValidators([Validators.required]);
        } else {
            scheduledDate?.clearValidators();
        }
        scheduledDate?.updateValueAndValidity();
    }

    onUpload(event: UploadEvent | any): void {
        // Server response is available in event.originalEvent.body
        const response = event.originalEvent?.body;
        if (response) {
            // Clear file uploader
            if (this.fileUploader) {
                this.fileUploader.clear();
            }

            // Add the uploaded file to gallery
            this.gallery.update((prev) => [
                ...prev,
                {
                    name: response.fileName,
                    type: CMS_GALLERY_TYPES.IMAGE
                }
            ]);

            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'File uploaded successfully'
            });
        } else {
            // Handle error case
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: response?.message || 'An error occurred while uploading the file.'
            });
        }
        this.uploading.set(false);
    }

    onBeforeUpload(event: UploadEvent | any): void {
        this.uploading.set(true);
    }

    /**
     * Triggers the file selection dialog when "Click to upload" text is clicked
     */
    triggerFileSelect(): void {
        // Find the hidden file input element in the fileUploader component and trigger click
        if (this.fileUploader) {
            const fileInput = this.fileUploader.el.nativeElement.querySelector('input[type="file"]');
            if (fileInput) {
                fileInput.click();
            }
        }
    }

    /**
     * Trigger file upload when the Add button is clicked.
     */
    onUploadClick(): void {
        if (this.fileUploader) {
            this.fileUploader.upload();
        }
    }

    /**
     * Validates a YouTube URL and adds it to the gallery
     */
    addYoutubeVideo(): void {
        // Reset validation state
        this.isInvalidYoutubeUrl.set(false);

        // Basic validation for YouTube URL
        if (!this.videoUrl || this.videoUrl.trim() === '') {
            this.isInvalidYoutubeUrl.set(true);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'YouTube URL cannot be empty'
            });
            return;
        }

        // Checks if the URL starts with https and contains 'youtube'
        const youtubeRegex = /^https:\/\/.*youtube.*/;
        if (!youtubeRegex.test(this.videoUrl)) {
            this.isInvalidYoutubeUrl.set(true);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please enter a valid YouTube URL.'
            });
            return;
        }
        // If the URL is valid, replace 'watch?v=' with 'embed/' for embedding
        this.videoUrl = this.videoUrl.replace('watch?v=', 'embed/');

        // Add to gallery
        this.gallery.update((prev) => [
            ...prev,
            {
                name: this.videoUrl,
                type: CMS_GALLERY_TYPES.YOUTUBE
            }
        ]);

        // Clear the input and show success message
        this.videoUrl = '';
        this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'YouTube video added to gallery'
        });
    }

    removeGalleryItem(item: ICmsGallery) {
        const index = this.gallery().findIndex((g) => g.id === item.id);
        if (index !== -1) {
            if (item.id) {
                this.isDeletingImage.set(true);
                this.cmsService
                    .deleteImage(item.id || 0)
                    .pipe(
                        finalize(() => {
                            this.isDeletingImage.set(false);
                        })
                    )
                    .subscribe(() => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'File removed from gallery'
                        });
                        this.gallery.update((prev) => {
                            const updated = [...prev];
                            updated.splice(index, 1);
                            return updated;
                        });
                    });
            } else {
                this.gallery.update((prev) => {
                    const updated = [...prev];
                    updated.splice(index, 1);
                    return updated;
                });
            }
        }
    }

    uploadOgImage(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        if (!this.validateImageFileSize(file, 'OG image')) {
            input.value = '';
            return;
        }
        console.log('file', file);
        this.uploadingOgImage.set(true);
        this.cmsService
            .uploadOgImage(this.contentId, file)
            .pipe(
                finalize(() => {
                    this.uploadingOgImage.set(false);
                    input.value = '';
                })
            )
            .subscribe({
                next: (response) => {
                    this.ogImageFileName.set(response.fileName);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'OG image uploaded successfully'
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to upload OG image'
                    });
                }
            });
    }

    deleteOgImage(): void {
        this.deletingOgImage.set(true);
        this.cmsService
            .deleteOgImage(this.contentId)
            .pipe(
                finalize(() => {
                    this.deletingOgImage.set(false);
                })
            )
            .subscribe({
                next: () => {
                    this.ogImageFileName.set(null);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'OG image deleted successfully'
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete OG image'
                    });
                }
            });
    }

    onGalleryDrop(event: CdkDragDrop<ICmsGallery[]>): void {
        this.gallery.update((prev) => {
            const updated = [...prev];
            moveItemInArray(updated, event.previousIndex, event.currentIndex);
            return updated;
        });
    }

    getFeatureBlocks(locale: string): ICmsFeatureBlock[] {
        const formGroup = this.getFormGroupAt(locale);
        return formGroup.get('featureBlocks')?.value || [];
    }

    openAddInfoBoxDialog(locale: string): void {
        const dialogRef = this.dialogService.open(AddInfoBoxDialogComponent, {
            width: '500px',
            data: {},
            showHeader: false
        });

        dialogRef?.onClose.subscribe((featureBlock: ICmsFeatureBlock) => {
            if (featureBlock) {
                this.addFeatureBlock(locale, featureBlock);
            }
        });
    }

    openEditInfoBoxDialog(locale: string, index: number): void {
        const featureBlocks = this.getFeatureBlocks(locale);
        const featureBlock = featureBlocks[index];

        const dialogRef = this.dialogService.open(AddInfoBoxDialogComponent, {
            header: 'Edit Info Box',
            width: '500px',
            data: { featureBlock }
        });

        dialogRef?.onClose.subscribe((updatedFeatureBlock: ICmsFeatureBlock) => {
            if (updatedFeatureBlock) {
                this.updateFeatureBlock(locale, index, updatedFeatureBlock);
            }
        });
    }

    addFeatureBlock(locale: string, featureBlock: ICmsFeatureBlock): void {
        const formGroup = this.getFormGroupAt(locale);
        const featureBlocks = [...(formGroup.get('featureBlocks')?.value || [])];

        featureBlock.sortOrder = featureBlocks.length + 1;
        featureBlocks.push(featureBlock);

        formGroup.patchValue({ featureBlocks });
    }

    updateFeatureBlock(locale: string, index: number, updatedFeatureBlock: ICmsFeatureBlock): void {
        const formGroup = this.getFormGroupAt(locale);
        const featureBlocks = [...(formGroup.get('featureBlocks')?.value || [])];

        updatedFeatureBlock.sortOrder = featureBlocks[index].sortOrder;
        featureBlocks[index] = updatedFeatureBlock;

        formGroup.patchValue({ featureBlocks });
    }

    removeFeatureBlock(locale: string, index: number): void {
        const formGroup = this.getFormGroupAt(locale);
        const featureBlocks = [...(formGroup.get('featureBlocks')?.value || [])];

        featureBlocks.splice(index, 1);

        // Update sort orders
        featureBlocks.forEach((block: ICmsFeatureBlock, i: number) => {
            block.sortOrder = i + 1;
        });

        formGroup.patchValue({ featureBlocks });
    }

    onFeatureBlockDrop(event: CdkDragDrop<ICmsFeatureBlock[]>, locale: string): void {
        const formGroup = this.getFormGroupAt(locale);
        const featureBlocks = [...(formGroup.get('featureBlocks')?.value || [])];

        moveItemInArray(featureBlocks, event.previousIndex, event.currentIndex);

        // Update sort orders
        featureBlocks.forEach((block: ICmsFeatureBlock, i: number) => {
            block.sortOrder = i + 1;
        });

        formGroup.patchValue({ featureBlocks });
    }

    openImagePreview(imageUrl: string): void {
        window.open(imageUrl, '_blank');
    }
}
